import React, { useState } from "react";
import { Box, Button, Typography, alpha } from "@mui/material";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import SyncIcon from "@mui/icons-material/Sync";
import GetAppIcon from "@mui/icons-material/GetApp";
import TuneIcon from "@mui/icons-material/Tune";
import { CSVLink } from "react-csv";
import { exportSelectedRowsPdf } from "../../utils/exportGridSelection";
import AdvancedExportDialog from "./AdvancedExportDialog";

export default function SearchResultsBulkBar({
  selectedCount = 0,
  selectedIds = [],
  rows = [],
  columns = [],
  exportTitle = "Resultados da Busca",
  exportFileName = "resultados_busca",
  exportContext = {},
  canBulk = false,
  canExport = false,
  onMerge,
  onDistribute,
  onChangeStatus,
  onClearSelection,
}) {
  const [advancedExportOpen, setAdvancedExportOpen] = useState(false);

  if (!selectedCount) return null;

  const csvData = canExport
    ? rows
        .filter((row) => selectedIds.includes(row.id))
        .map((row) => {
          const csvRow = {};
          columns.forEach((col) => {
            const label = col.headerName || col.field;
            if (typeof col.valueGetter === "function") {
              try {
                csvRow[label] = col.valueGetter(row[col.field], row, col, null) ?? row[col.field] ?? "";
              } catch {
                csvRow[label] = row[col.field] ?? "";
              }
            } else {
              csvRow[label] = row[col.field] ?? "";
            }
          });
          return csvRow;
        })
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: 1,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 0.5, mr: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary" component="span">
          <strong>{selectedCount}</strong> selecionado{selectedCount === 1 ? "" : "s"}
        </Typography>
        {canBulk && selectedCount > 0 && (
          <Typography component="span" variant="caption" color="warning.dark">
            · ações em massa são irreversíveis
          </Typography>
        )}
      </Box>

      {canBulk && (
        <>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CallMergeIcon />}
            onClick={onMerge}
            disabled={selectedCount < 2}
            sx={{ textTransform: "none" }}
          >
            Unificar
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={onDistribute}
            sx={{ textTransform: "none" }}
          >
            Distribuir
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={onChangeStatus}
            sx={{ textTransform: "none" }}
          >
            Alterar
          </Button>
        </>
      )}

      {canExport && (
        <>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={() =>
              exportSelectedRowsPdf({
                rows,
                columns,
                selectedIds,
                title: exportTitle,
                fileName: exportFileName,
              })
            }
            sx={{ textTransform: "none" }}
          >
            Exportar PDF
          </Button>

          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            sx={{ textTransform: "none" }}
          >
            <CSVLink
              data={csvData}
              filename={`${exportFileName}.csv`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Exportar CSV
            </CSVLink>
          </Button>

          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<TuneIcon />}
            onClick={() => setAdvancedExportOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Exportação avançada
          </Button>

          <AdvancedExportDialog
            open={advancedExportOpen}
            onClose={() => setAdvancedExportOpen(false)}
            selectedIds={selectedIds}
            rows={rows}
            exportTitle={exportTitle}
            exportFileName={exportFileName}
            exportContext={exportContext}
          />
        </>
      )}

      <Box sx={{ flex: 1 }} />

      <Button size="small" onClick={onClearSelection} sx={{ textTransform: "none" }}>
        Limpar seleção
      </Button>
    </Box>
  );
}
